// src/Pages/BookingResult/BookingResult.jsx
import React, { useMemo, useState, useCallback } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { API_BASE, getAuthToken } from "../../services/api.js";

/** Päättele näkymä URL-polusta */
function useVariant() {
  const { pathname } = useLocation();
  if (/(\/(varaus\/)?kiitos)(\/)?$/i.test(pathname)) return "confirmed";
  if (/(\/(varaus\/)?peruttu)(\/)?$/i.test(pathname)) return "cancelled";
  return "unknown";
}

/** YYYY-MM-DD -> “keskiviikko 1. tammikuuta 2026” (fi-FI) */
function formatFinnishDate(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ""))) return null;
  try {
    const [y, m, d] = ymd.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return new Intl.DateTimeFormat("fi-FI", {
      dateStyle: "full",
      timeZone: "Europe/Helsinki",
    }).format(date);
  } catch {
    return ymd;
  }
}

export default function BookingResult() {
  const variant = useVariant();
  const [params] = useSearchParams();
  const [downloading, setDownloading] = useState(false);

  // ---- URL-parametrit
  const dateParam = params.get("date") || "";
  const bandParamRaw = params.get("band") || "";
  const bandParam = useMemo(
    () => bandParamRaw.replace(/\+/g, " "),
    [bandParamRaw]
  );
  const bandSlug = params.get("band_slug") || "";

  // Anonyymin latauksen kertakäyttöavain (kiitossivun linkistä)
  const kParam = params.get("k") || "";

  // Hyväksytään useampi nimi laskun id:lle, mutta vaaditaan positiivinen numero
  const invoiceIdParam =
    params.get("invoice_id") ||
    params.get("invoiceId") ||
    params.get("inv") ||
    params.get("iid") ||
    "";
  const invoiceId = useMemo(() => {
    const s = String(invoiceIdParam || "").trim();
    if (!/^\d+$/.test(s)) return null; // vain numeroita
    const n = Number(s);
    return Number.isInteger(n) && n > 0 ? n : null; // positiivinen int
  }, [invoiceIdParam]);

  const dateLong = formatFinnishDate(dateParam);

  const isConfirmed = variant === "confirmed";
  const isCancelled = variant === "cancelled";
  const hasInvoice = useMemo(() => {
    const jwt = getAuthToken?.();
    return (
      isConfirmed &&
      Number.isInteger(invoiceId) &&
      invoiceId > 0 &&
      (!!kParam || !!jwt)
    );
  }, [isConfirmed, invoiceId, kParam]);

  // ---- ENV (Vite/CRA)
  const env =
    (typeof import.meta !== "undefined" && import.meta.env) || process.env || {};
  const SHOW_IBAN =
    String(
      env.VITE_SHOW_IBAN_ON_CONFIRM ?? env.REACT_APP_SHOW_IBAN_ON_CONFIRM ?? ""
    ).toLowerCase() === "true";
  const COMPANY_IBAN = String(
    env.VITE_COMPANY_IBAN ?? env.REACT_APP_COMPANY_IBAN ?? ""
  );
  const COMPANY_NAME = String(
    env.VITE_COMPANY_NAME ?? env.REACT_APP_COMPANY_NAME ?? "Buukkeri"
  );

  // ---- Otsikot & kuvaukset
  const title = isConfirmed
    ? hasInvoice
      ? "Kiitos varauksesta – voit nyt ladata laskun"
      : "Kiitos varauksesta – varaus on vahvistettu"
    : isCancelled
    ? "Varaus on peruttu"
    : "Varaustilanne";

  const intro = isConfirmed
    ? hasInvoice
      ? "Varaus on nyt vahvistettu ja päivä on merkitty esiintyjän kalenteriin. Varaus pysyy voimassa, kun varausmaksu on maksettu tämän laskun mukaisesti. Loppumaksu sovitaan ja hoidetaan suoraan esiintyjän kanssa."
      : "Varaus on nyt vahvistettu. Lähetämme teille pian varausmaksun laskun. Kun varausmaksu on maksettu, varaus on lopullisesti voimassa. Loppumaksu sovitaan ja hoidetaan suoraan esiintyjän kanssa."
    : isCancelled
    ? "Varaus on peruttu toivomanne mukaisesti."
    : "Tämän linkin tietoihin liittyy epäselvyyttä. Jos epäilette virhettä, olkaa yhteydessä asiakaspalveluun.";

  const nextStepsConfirmed = hasInvoice
  ? [
      "Lataa varausmaksun lasku alta (PDF). Latauslinkki on voimassa 72 tunnin ajan, ja laskun voi ladata useamman kerran.",
      "Maksa varausmaksu heti laskun saatuanne (laskun eräpäivä on sama päivä), jotta varaus pysyy vahvistettuna.",
      "Loppuosa sovitusta hinnasta maksetaan suoraan esiintyjälle yhteisen sopimuksenne mukaan.",
    ]
    : [
        "Saatte varausmaksun laskun sähköpostitse pian. Kun lasku on maksettu, varaus on lopullisesti vahvistettu.",
        "Loppuosa sovitusta hinnasta maksetaan suoraan esiintyjälle yhteisen sopimuksenne mukaan.",
      ];

  const infoParagraphsConfirmed = hasInvoice
    ? [
        "Varausmaksu vähennetään esiintyjän kanssa sovittavasta kokonaishinnasta. Buukkeri ei ole osapuolena loppumaksun maksuliikenteessä – se sovitaan ja hoidetaan suoraan asiakkaan ja esiintyjän välillä.",
        "Jos laskun lataus ei onnistu tai vahvistitte varauksen vahingossa, olkaa viipymättä yhteydessä – autamme tilanteen korjaamisessa: info@buukkeri.fi tai 044 351 9265.",
      ]
    : [
        "Varausmaksu vähennetään esiintyjän kanssa sovittavasta kokonaishinnasta. Buukkeri ei ole osapuolena loppumaksun maksuliikenteessä – se sovitaan ja hoidetaan suoraan asiakkaan ja esiintyjän välillä.",
        "Jos vahvistus tapahtui vahingossa tai haluatte muuttaa varausta, olkaa viipymättä yhteydessä: info@buukkeri.fi tai 044 351 9265.",
      ];

  const infoParagraphsCancelled = [
    "Jos peruitte varauksen vahingossa tai haluatte palata asiaan, ottakaa yhteyttä – avaamme varauksen uudelleen, mikäli ajankohta on yhä vapaa.",
    "Tarvitsetteko toisen päivän tai eri esiintyjän? Autamme mielellämme sopivan vaihtoehdon etsimisessä: info@buukkeri.fi tai 044 351 9265.",
  ];

  // ---- Hiljainen laskun lataus
  const handleInvoiceDownload = useCallback(async () => {
    if (!Number.isInteger(invoiceId) || invoiceId <= 0) {
      toast.error(
        "Latauslinkkiä ei voitu muodostaa (virheellinen laskun tunnus).",
        { containerId: "global" }
      );
      return;
    }

    setDownloading(true);

    try {
      // 1) Pyydä issue-token (kertakäyttöinen latauslinkki)
      const issueUrl = `${API_BASE}/invoices/${encodeURIComponent(
        invoiceId
      )}/issue-token`;

      const headers = {
        Accept: "application/json",
      };

      let body = undefined;
      let credentials = "include";

      if (kParam) {
        // Anonyymi polku: käytetään kiitossivun k-paramia, ei JWT:tä
        body = JSON.stringify({ k: kParam });
        headers["Content-Type"] = "application/json";
        credentials = "omit";
      } else {
        // Kirjautunut käyttäjä (esim. dashboardista): Authorization-header
        const jwt = getAuthToken?.();
        if (jwt) {
          headers.Authorization = `Bearer ${jwt}`;
        }
      }

      const issueRes = await fetch(issueUrl, {
        method: "POST",
        credentials,
        headers,
        body,
      });

      const raw = await issueRes.text();
      let json = null;
      try {
        json = raw ? JSON.parse(raw) : null;
      } catch {
        json = null;
      }

      if (!issueRes.ok || !json?.ok) {
        // Tunnetut koodit
        if (issueRes.status === 401 || issueRes.status === 403) {
          toast.error(
            kParam
              ? "Latauslinkin muodostus estyi (401/403). Linkki on mahdollisesti vanhentunut tai virheellinen."
              : "Latauslinkin muodostus estyi (401/403). Kirjaudu sisään tai käytä kiitossivun linkkiä.",
            { containerId: "global" }
          );
        } else if (issueRes.status === 404) {
          toast.error("Laskua ei löytynyt (404).", { containerId: "global" });
        } else {
          const msg = json?.error || issueRes.status;
          toast.error(`Laskun lataus epäonnistui (${msg}).`, {
            containerId: "global",
          });
        }
        return;
      }

      const directUrl = json?.url;
      const token = json?.token;

      if (!directUrl && !token) {
        toast.error("Latauslinkkiä ei voitu muodostaa (puuttuu token).", {
          containerId: "global",
        });
        return;
      }

      const downloadUrl =
        directUrl ||
        `${API_BASE}/invoices/${encodeURIComponent(
          invoiceId
        )}/download?token=${encodeURIComponent(token)}`;

      // 2) Lataa varsinainen PDF hiljaisesti
      const pdfRes = await fetch(downloadUrl, { method: "GET" });

      const contentType = (pdfRes.headers.get("content-type") || "").toLowerCase();

      if (
        !pdfRes.ok ||
        (!contentType.includes("pdf") && !contentType.includes("octet-stream"))
      ) {
        // Jos token on esim. käytetty / vanhentunut, backend palauttaa JSONin → älä lataa sitä PDF:nä
        toast.error(
          "Laskun lataus epäonnistui. Linkki voi olla vanhentunut tai virheellinen.",
          { containerId: "global" }
        );
        return;
      }

      const blob = await pdfRes.blob();

      // 3) Luo blob-URL ja käynnistä lataus ilman sivun vaihtumista
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Buukkeri-varausmaksu-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Laskun lataus epäonnistui:", err);
      toast.error("Laskun lataus epäonnistui. Yritä hetken päästä uudelleen.", {
        containerId: "global",
      });
    } finally {
      setDownloading(false);
    }
  }, [invoiceId, kParam]);

  // Yhtenäinen nappi-tyyli: ei palleroita isoilla ruuduilla
  const btn =
    "inline-flex items-center justify-center rounded-xl font-extrabold text-sm h-12 px-5 transition " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";
  const btnWide = "w-full sm:w-auto"; // mobiilissa täysleveä, ≥sm automaatti

  return (
    <>
      <Helmet>
        <title>{title} – Buukkeri</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta
          name="description"
          content={
            isConfirmed
              ? hasInvoice
                ? "Varaus on vahvistettu. Lataa varausmaksun lasku (PDF), maksa varausmaksu ja sovi loppumaksu suoraan esiintyjän kanssa."
                : "Varaus on vahvistettu. Saatte varausmaksun laskun pian, ja loppumaksu sovitaan suoraan esiintyjän kanssa."
              : isCancelled
              ? "Varaus on peruttu. Ota yhteyttä, jos haluatte sopia uuden ajankohdan tai esiintyjän."
              : "Varaustilanne ja ohjeet jatkoa varten."
          }
        />
      </Helmet>

      <main className="relative w-full min-h-[60vh] pb-16 mt-24">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <section
            className="rounded-2xl ring-1 backdrop-blur-md shadow-xl
                       bg-[#0c0c12]/70 ring-white/10 text-white"
            aria-labelledby="booking-result-title"
          >
            <div className="p-6 sm:p-8">
              <h1
                id="booking-result-title"
                className="text-2xl sm:text-3xl font-extrabold text-teal-300"
              >
                {title}
              </h1>

              <p className="mt-2 text-base sm:text-lg text-white/85">{intro}</p>

              {(dateLong || bandParam) && (
                <div className="mt-6 divide-y divide-white/10">
                  {dateLong && (
                    <div className="py-3 text-sm sm:text-base">
                      <span className="inline-block w-36 sm:w-40 text-teal-200/90">
                        Päivämäärä
                      </span>
                      <span className="text-white">{dateLong}</span>
                    </div>
                  )}
                  {bandParam && (
                    <div className="py-3 text-sm sm:text-base">
                      <span className="inline-block w-36 sm:w-40 text-teal-200/90">
                        Esiintyjä
                      </span>
                      {bandSlug ? (
                        <Link
                          to={`/esiintyja/${bandSlug}`}
                          className="text-white underline"
                        >
                          {bandParam}
                        </Link>
                      ) : (
                        <span className="text-white">{bandParam}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isConfirmed && SHOW_IBAN && COMPANY_IBAN && (
                <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4 sm:p-5 text-sm sm:text-base text-white/85">
                  <h2 className="text-white font-semibold text-base sm:text-lg">
                    Maksutiedot
                  </h2>
                  <p className="mt-2">
                    Varausmaksu maksetaan {COMPANY_NAME}ille. IBAN:{" "}
                    <span className="font-semibold tracking-wide">
                      {COMPANY_IBAN}
                    </span>
                    . Tämä tili on tarkoitettu vain varausmaksua varten; loppumaksu maksetaan suoraan esiintyjälle.
                  </p>
                </div>
              )}

              {isConfirmed && (
                <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4 sm:p-5 text-sm sm:text-base text-white/85">
                  <h2 className="text-white font-semibold text-base sm:text-lg">
                    Seuraavat askeleet
                  </h2>
                  <ol className="list-decimal pl-5 mt-2 space-y-2">
                    {nextStepsConfirmed.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4 sm:p-5 text-sm sm:text-base text-white/85">
                {isConfirmed &&
                  infoParagraphsConfirmed.map((t, i) => (
                    <p key={i} className={i ? "mt-3" : ""}>
                      {t}
                    </p>
                  ))}
                {isCancelled &&
                  infoParagraphsCancelled.map((t, i) => (
                    <p key={i} className={i ? "mt-3" : ""}>
                      {t}
                    </p>
                  ))}
                {!isConfirmed && !isCancelled && (
                  <p>
                    Jos tarvitsette apua, olkaa yhteydessä osoitteeseen{" "}
                    <a href="mailto:info@buukkeri.fi" className="underline">
                      info@buukkeri.fi
                    </a>{" "}
                    tai numeroon{" "}
                    <a href="tel:+358443519265" className="underline">
                      044 351 9265
                    </a>
                    .
                  </p>
                )}
              </div>

              {/* --- CTA:t: mobiilissa pinottu, ≥sm rivitetty --- */}
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
                {isConfirmed ? (
                  <>
                    {/* 1) Lataa lasku (primääri) */}
                    {hasInvoice ? (
                      <div className="sm:order-1">
                        <button
                          type="button"
                          onClick={handleInvoiceDownload}
                          disabled={downloading}
                          aria-busy={downloading ? "true" : "false"}
                          className={`${btn} ${btnWide} bg-white text-[#0A0014] hover:opacity-90 disabled:opacity-60`}
                        >
                          {downloading ? "Ladataan…" : "Lataa lasku (PDF)"}
                        </button>
                        <p className="mt-2 text-xs text-white/70">
                        
                        </p>

                      </div>
                    ) : null}

                    {/* 2) Katso esiintyjän sivu */}
                    {bandSlug ? (
                      <Link
                        to={`/esiintyja/${bandSlug}`}
                        className={`${btn} ${btnWide} bg-[#a78bfa] text-[#0A0014] hover:opacity-90 sm:order-2`}
                      >
                        Katso esiintyjän sivu
                      </Link>
                    ) : null}

                    {/* 3) Kysy lisää */}
                    <a
                      href="mailto:info@buukkeri.fi"
                      className={`${btn} ${btnWide} bg-[#31FFD9] text-[#0A0014] hover:opacity-90 sm:order-3`}
                    >
                      Kysy lisää
                    </a>

                    {/* 4) Palaa etusivulle */}
                    <Link
                      to="/"
                      className={`${btn} ${btnWide} bg-[#FF2D95] text-[#0A0014] hover:opacity-90 sm:order-4`}
                    >
                      Palaa etusivulle
                    </Link>
                  </>
                ) : isCancelled ? (
                  <>
                    <Link
                      to="/otayhteytta"
                      className={`${btn} ${btnWide} bg-[#31FFD9] text-[#0A0014] hover:opacity-90`}
                    >
                      Ota yhteyttä
                    </Link>
                    <Link
                      to="/"
                      className={`${btn} ${btnWide} bg-[#FF2D95] text-[#0A0014] hover:opacity-90`}
                    >
                      Palaa etusivulle
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/otayhteytta"
                    className={`${btn} ${btnWide} bg-[#31FFD9] text-[#0A0014] hover:opacity-90`}
                  >
                    Ota yhteyttä
                  </Link>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
